import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

export interface LessonProgress {
    lesson_id: string;
    lesson_title: string;
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
    progress_percentage: number;
    started_at?: string;
    last_accessed_at?: string;
    completed_at?: string;
    time_spent_seconds: number;
    bookmarked: boolean;
    metadata?: Record<string, any>;
}

export interface ModuleProgress {
    module_id: string;
    module_title: string;
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
    progress_percentage: number;
    time_spent_seconds: number;
    last_accessed_at?: string;
    completed_at?: string;
    lesson_progress?: LessonProgress[];
}

export interface ProgressData {
    progress_percentage: number;
    total_time_spent_seconds: number;
    started_at?: string;
    last_accessed_at?: string;
    completed_at?: string;
    is_mandatory: boolean;
    deadline_date?: string;
    module_progress?: ModuleProgress[];
}

export interface GamificationMetricsResponse {
    xp: number;
    level: number;
    streak: number;
    baseXP: number;
    bonusXP: number;
    levelProgress: number;
    nextLevelXP: number;
}

export interface CourseMetrics extends GamificationMetricsResponse {
    course_id: string;
    course_title?: string;
    course_status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
}

export interface CertificationMetrics {
    certification_id: string;
    certification_title: string;
    xp: number;
    level: number;
    baseXP: number;
    bonusXP: number;
    nextLevelXP: number;
    passed: boolean;
}

export interface EarnedBadge {
    badge_id: string;
    badge_name: string;
    description: string;
    icon_type: string;
    icon_color: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
    xp_required: number;
    category: string;
    badge_url?: string;
}

export interface MyCourseMetricsResponse {
    total_user_xp: number;
    earned_badges: EarnedBadge[];
    new_badges: EarnedBadge[];
    course_id: string;
    course_title: string;
    course_xp: number;
    course_level: number;
    course_streak: number;
    course_base_xp: number;
    course_bonus_xp: number;
    course_level_progress: number;
    course_next_level_xp: number;
    course_status?: string;
}

export interface CertificationMetricsData {
    certification_id: string;
    certification_title: string;
    xp: number;
    level: number;
    streak: number;
    baseXP: number;
    bonusXP: number;
    levelProgress: number;
    nextLevelXP: number;
    certification_status?: string;
}

export interface MyCertificationMetricsResponse {
    total_user_xp: number;
    earned_badges: EarnedBadge[];
    new_badges: EarnedBadge[];
    certification_metrics: CertificationMetricsData;
}

export interface AcknowledgeBadgesRequest {
    badge_ids: string[];
    source_type?: 'COURSE' | 'CERTIFICATION';
    source_id?: string;
}

export interface AcknowledgeBadgesResponse {
    acknowledged_count: number;
}

// Badge Metrics API Response
export interface BadgeMetricsResponse {
    user_xp: number;
    rank: number;
    total_badge_count: number;
    level?: number;
    next_level_xp?: number;
    course_certificates_count?: number;
    certification_certificates_count?: number;
    learning_path_certificates_count?: number;
}

// User Badge (earned) from API
export interface UserBadge {
    badge_id: string;
    badge_name: string;
    description: string;
    icon_type: string;
    icon_color: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
    xp_required: number;
    category: string;
    badge_url?: string;
    earned_at?: string;
    points?: number;
}

// Locked Badge from API
export interface LockedBadge {
    badge_id: string;
    badge_name: string;
    description: string;
    icon_type: string;
    icon_color: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
    xp_required: number;
    category: string;
    badge_url?: string;
    points?: number;
    user_xp?: number;
    progress_percentage?: number;
}

// List User Badges Request
export interface ListUserBadgesRequest {
    search?: string;
}

// Certificate from API (certification certificates)
export interface CertificationCertificate {
    _id: string;
    user_id: string;
    certification_id: string;
    tracking_id: string;
    exam_attempt_id: string;
    certificate_number: string;
    verification_token: string;
    verification_url: string;
    tier: 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM';
    issued_date: string;
    holder_info: {
        user_id: string;
        user_name: string;
        user_email: string;
    };
    metadata: {
        certification_name: string;
        certification_description: string;
        score_achieved: number;
        percentage_achieved: number;
        passing_percentage: number;
        exam_date: string;
    };
    pdf_url: string;
    pdf_key: string;
    status: 'ACTIVE' | 'REVOKED';
    issued_by: string;
    issued_by_name: string;
    createdAt: string;
    updatedAt: string;
    revocation_reason?: string;
    revoked_at?: string;
    revoked_by?: string;
    certification_title: string;
    banner_image_url?: string;
}

// Course Certificate from API
export interface CourseCertificate {
    _id: string;
    user_id: string;
    course_id: string;
    tracking_id: string;
    assignment_id: string;
    certificate_number: string;
    verification_token: string;
    verification_url: string;
    issued_date: string;
    holder_info: {
        user_id: string;
        user_name: string;
        user_email: string;
    };
    metadata: {
        course_title: string;
        course_description: string;
        time_spent_seconds: number;
        completion_date: string;
    };
    pdf_url: string;
    pdf_key: string;
    status: 'ACTIVE' | 'REVOKED';
    issued_by: string;
    issued_by_name: string;
    created_at: string;
    updated_at: string;
    course_title: string;
    banner_image_url?: string;
}

// Learning Path Certificate from API
export interface LearningPathCertificate {
    _id: string;
    user_id: string;
    learning_path_id: string;
    tracking_id: string;
    assignment_id: string;
    certificate_number: string;
    verification_token: string;
    verification_url: string;
    issued_date: string;
    holder_info: {
        user_id: string;
        user_name: string;
        user_email: string;
    };
    metadata: {
        learning_path_title: string;
        learning_path_description: string;
        total_courses: number;
        completed_courses: number;
        total_time_spent_seconds: number;
        total_time_spent_hours: number;
        average_progress_percentage: number;
        completion_date: string;
        courses_completed?: Array<{
            course_id: string;
            course_title: string;
            completed_at: string;
            progress_percentage: number;
        }>;
    };
    pdf_url: string;
    pdf_key: string;
    status: 'ACTIVE' | 'REVOKED';
    issued_by: string;
    issued_by_name: string;
    created_at: string;
    updated_at: string;
    learning_path_title: string;
    banner_image_url?: string;
}

// User Certificates Response
export interface UserCertificatesResponse {
    course_certificates: CourseCertificate[];
    certification_certificates: CertificationCertificate[];
    learning_path_certificates: LearningPathCertificate[];
}

export interface UserLeaderboardData {
    user_id: string;
    username: string;
    email: string;
    image?: string;
    total_xp: number;
    total_level: number;
    max_streak: number;
    streak: number;
    longest_streak: number;
    total_base_xp: number;
    total_bonus_xp: number;
    average_level_progress: number;
    rank: number;
    courses: CourseMetrics[];
    all_courses: CourseMetrics[];
    certifications: CertificationMetrics[];
    total_courses: number;
    total_certifications: number;
}

export interface LeaderboardResponse {
    total_users: number;
    result_count: number;
    skip: number;
    limit: number;
    leaderboard: UserLeaderboardData[];
    top_performers?: UserLeaderboardData[];
}

export interface LeaderboardQueryParams {
    page?: number;
    limit?: number;
    search?: string;
}

// API Response wrapper
interface ApiResponse<T> {
    success: boolean;
    code: number;
    data: T;
    meta_data: string;
}

// User performance API response structure
interface UserPerformanceApiResponse {
    courses: CourseMetrics[];
    certifications: CertificationMetrics[];
    total_courses: number;
    total_certifications: number;
    rank: number;
    summary: {
        total_xp: number;
        total_courses: number;
        total_certifications: number;
        total_level: number;
        max_streak: number;
        streak: number;
        longest_streak: number;
    };
}

class LeaderBoardService {
    async getAllLeaderBoard(params?: LeaderboardQueryParams): Promise<LeaderboardResponse> {
        try {
            // Build request body with page and limit as numbers
            const body = {
                page: params?.page ? Number(params.page) : 1,
                limit: params?.limit ? Number(params.limit) : 10,
                ...(params?.search && { search: params.search })
            };

            const response = await apiClient.post<ApiResponse<{
                data: UserLeaderboardData[];
                total: number;
                page: number;
                limit: number;
                top_performers?: UserLeaderboardData[];
            }>>(
                API_ENDPOINTS.LEADERBOARD.LIST,
                body
            );
            const apiData = response.data.data;
            // Map API response to LeaderboardResponse interface
            return {
                leaderboard: apiData.data || [],
                total_users: apiData.total || 0,
                result_count: apiData.data?.length || 0,
                skip: ((params?.page || 1) - 1) * (params?.limit || 10),
                limit: apiData.limit,
                top_performers: apiData.top_performers || []
            };
        } catch (error) {
            console.error('Failed to fetch all leaderboard:', error);
            throw error;
        }
    }

    async getUserLeaderBoard(): Promise<UserLeaderboardData> {
        try {
            const response = await apiClient.post<ApiResponse<UserPerformanceApiResponse>>(
                API_ENDPOINTS.LEADERBOARD.USER,
                {}
            );
            const apiData = response.data.data;
            const courses = apiData.courses || [];
            const certifications = apiData.certifications || [];
            const summary = apiData.summary;

            // Calculate total base and bonus XP from courses and certifications
            const coursesBaseXP = courses.reduce((sum, course) => sum + (course.baseXP || 0), 0);
            const coursesBonusXP = courses.reduce((sum, course) => sum + (course.bonusXP || 0), 0);
            const certsBaseXP = certifications.reduce((sum, cert) => sum + (cert.baseXP || 0), 0);
            const certsBonusXP = certifications.reduce((sum, cert) => sum + (cert.bonusXP || 0), 0);
            const total_base_xp = coursesBaseXP + certsBaseXP;
            const total_bonus_xp = coursesBonusXP + certsBonusXP;

            // Calculate average level progress from courses and certifications
            const totalItems = courses.length + certifications.length;
            const coursesLevelProgress = courses.reduce((sum, course) => sum + (course.levelProgress || 0), 0);
            const certsLevelProgress = certifications.reduce((sum, cert) => sum + (cert.passed ? 100 : Math.min(((cert.xp || 0) / (cert.nextLevelXP || 100)) * 100, 100)), 0);
            const average_level_progress = totalItems > 0
                ? Math.round((coursesLevelProgress + certsLevelProgress) / totalItems)
                : 0;

            // Map to UserLeaderboardData
            return {
                user_id: '',
                username: '',
                email: '',
                total_xp: summary?.total_xp || 0,
                total_level: summary?.total_level || 1,
                max_streak: summary?.max_streak || 0,
                streak: summary?.streak || 0,
                longest_streak: summary?.longest_streak || 0,
                total_base_xp,
                total_bonus_xp,
                average_level_progress,
                rank: apiData.rank || 0,
                courses: courses,
                all_courses: courses,
                certifications: certifications,
                total_courses: apiData.total_courses || courses.length,
                total_certifications: apiData.total_certifications || certifications.length
            };
        } catch (error) {
            console.error('Failed to fetch user leaderboard:', error);
            throw error;
        }
    }

    async getMyCourseMetrics(courseId: string): Promise<MyCourseMetricsResponse> {
        try {
            const response = await apiClient.get<ApiResponse<MyCourseMetricsResponse>>(
                API_ENDPOINTS.LEADERBOARD.MY_COURSE_METRICS,
                { params: { course_id: courseId } }
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch course gamification metrics:', error);
            throw error;
        }
    }

    async getMyCertificationMetrics(certificationId: string): Promise<MyCertificationMetricsResponse> {
        try {
            const response = await apiClient.get<ApiResponse<MyCertificationMetricsResponse>>(
                API_ENDPOINTS.LEADERBOARD.MY_CERTIFICATION_METRICS,
                { params: { certification_id: certificationId } }
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch certification gamification metrics:', error);
            throw error;
        }
    }

    async acknowledgeBadges(request: AcknowledgeBadgesRequest): Promise<AcknowledgeBadgesResponse> {
        try {
            const response = await apiClient.post<ApiResponse<AcknowledgeBadgesResponse>>(
                API_ENDPOINTS.LEADERBOARD.ACKNOWLEDGE_BADGES,
                request
            );
            return response.data.data;
        } catch (error) {
            console.error('Failed to acknowledge badges:', error);
            throw error;
        }
    }

    async getBadgeMetrics(): Promise<BadgeMetricsResponse> {
        try {
            const response = await apiClient.get<ApiResponse<BadgeMetricsResponse>>(
                API_ENDPOINTS.LEADERBOARD.BADGES_METRICS
            );
            return response.data.data || {
                user_xp: 0,
                rank: 0,
                total_badge_count: 0
            };
        } catch (error) {
            console.error('Failed to fetch badge metrics:', error);
            // Return default values instead of throwing
            return {
                user_xp: 0,
                rank: 0,
                total_badge_count: 0
            };
        }
    }

    async listUserBadges(search?: string): Promise<UserBadge[]> {
        try {
            const body: ListUserBadgesRequest = {};
            if (search) {
                body.search = search;
            }
            const response = await apiClient.post<ApiResponse<UserBadge[] | { badges: UserBadge[]; data: UserBadge[] }>>(
                API_ENDPOINTS.LEADERBOARD.BADGES_LIST,
                body
            );
            const data = response.data.data;
            // Handle different response structures
            if (Array.isArray(data)) {
                return data;
            }
            if (data && typeof data === 'object') {
                // Try common nested structures
                if (Array.isArray((data as any).badges)) return (data as any).badges;
                if (Array.isArray((data as any).data)) return (data as any).data;
            }
            return [];
        } catch (error) {
            console.error('Failed to fetch user badges:', error);
            return [];
        }
    }

    async getLockedBadges(): Promise<LockedBadge[]> {
        try {
            const response = await apiClient.get<ApiResponse<LockedBadge[] | { badges: LockedBadge[]; data: LockedBadge[] }>>(
                API_ENDPOINTS.LEADERBOARD.BADGES_LOCKED
            );
            const data = response.data.data;
            // Handle different response structures
            if (Array.isArray(data)) {
                return data;
            }
            if (data && typeof data === 'object') {
                // Try common nested structures
                if (Array.isArray((data as any).badges)) return (data as any).badges;
                if (Array.isArray((data as any).data)) return (data as any).data;
            }
            return [];
        } catch (error) {
            console.error('Failed to fetch locked badges:', error);
            return [];
        }
    }

    async getUserCertificates(): Promise<UserCertificatesResponse> {
        try {
            const response = await apiClient.post<ApiResponse<any>>(
                API_ENDPOINTS.LEADERBOARD.BADGES_LIST,
                {}
            );
            const data = response.data.data;
            // Extract certificates from the response
            if (data && typeof data === 'object' && data.certificates) {
                return {
                    course_certificates: data.certificates.course_certificates || [],
                    certification_certificates: data.certificates.certification_certificates || [],
                    learning_path_certificates: data.certificates.learning_path_certificates || []
                };
            }
            return {
                course_certificates: [],
                certification_certificates: [],
                learning_path_certificates: []
            };
        } catch (error) {
            console.error('Failed to fetch user certificates:', error);
            return {
                course_certificates: [],
                certification_certificates: [],
                learning_path_certificates: []
            };
        }
    }
}

export const Leaderboardservice = new LeaderBoardService();